import React from 'react';

const Section = ({ title, children }) => (
  <div>
    <h2 className="text-[22px] sm:text-2xl md:text-3xl font-medium mb-3 sm:mb-4 leading-snug" style={{ fontFamily: "'Playfair Display', serif" }}>
      {title}
    </h2>
    {children}
  </div>
);

const RefundPolicy = () => {
  return (
    <div className="max-w-4xl mx-auto px-5 sm:px-6 py-10 sm:py-16 text-[#1a1a1a]">

      <h1 className="text-[32px] sm:text-4xl md:text-5xl font-medium mb-5 sm:mb-8 leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
        Refund Policy
      </h1>

      <p className="text-sm mb-7 sm:mb-10 opacity-70">Last updated: October 6, 2025</p>

      <div className="prose prose-lg max-w-none text-[15px] sm:text-[16px] leading-relaxed space-y-7 sm:space-y-8">

        <p>
          At Aishcraft, we want you to be completely satisfied with your purchase. If for any reason
          you are not happy, we have outlined our refund and returns policy below. Please read it
          carefully before making a purchase, and do not hesitate to contact us at hello@aishcraft.com
          if you have any questions.
        </p>

        {/* 1 */}
        <Section title="Returns">
          <p>
            We really hope you love every Aishcraft item you purchase. However, if you do need to make
            a return, you are welcome to do so within <strong>30 days of receipt</strong>.
          </p>
          <p className="mt-3">
            All returned items must be sent back to us <strong>unopened, unused and in their original
            condition</strong> including all original packaging, tags, and accessories. We reserve the
            right to refuse an exchange or refund if items are non-saleable.
          </p>
          <p className="mt-3">
            Please note, if 30 days have expired then unfortunately we are unable to accept your return.
            We strongly recommend retaining your proof of purchase for all orders.
          </p>
          <p className="mt-3">
            To initiate a return, please email us at hello@aishcraft.com with your order number and
            reason for return before sending any items back. Returns sent without prior approval may
            not be processed.
          </p>
          <ul className="list-disc pl-5 space-y-2 mt-3">
            <li>Items must be in original, unused condition with all packaging intact.</li>
            <li>Returns must be requested within 30 days of the delivery date.</li>
            <li>Customised or personalised items cannot be returned unless faulty.</li>
            <li>Gift Cards are non-returnable and non-refundable.</li>
            <li>Digital products and downloadable items are non-refundable once accessed.</li>
            <li>We reserve the right to refuse a refund if the item does not meet our return conditions.</li>
          </ul>
        </Section>

        {/* 2 */}
        <Section title="How to Return Your Items">
          <p>
            To return your items, please use the cheapest available shipping option to avoid any extra
            cost to you and allow <strong>21 working days</strong> for the return to be received into
            our warehouse.
          </p>
          <p className="mt-3">
            Once we receive and inspect your return, we will email you to notify you whether your
            return has been accepted. If approved, we will process your refund to your original payment
            method. Once you have received an email confirming your refund, please allow
            <strong> 5–10 working days</strong> for your refund to appear back in your account.
          </p>
          <ul className="list-disc pl-5 space-y-2 mt-3">
            <li>Email us at hello@aishcraft.com to request a return authorisation before shipping.</li>
            <li>Pack items securely in their original packaging to avoid damage in transit.</li>
            <li>Use a tracked shipping method and retain your proof of postage.</li>
            <li>Aishcraft is not responsible for items lost or damaged in return transit.</li>
            <li>Return shipping costs are the responsibility of the customer unless the item is faulty.</li>
          </ul>
        </Section>

        {/* 3 */}
        <Section title="Faulty Goods">
          <p>
            Goods are classified as faulty if they are not of satisfactory quality, fit for purpose,
            or as described. Please note that items which are damaged as a result of normal wear and
            tear, by accident, or through misuse will not be considered faulty.
          </p>
          <p className="mt-3">
            If your item is faulty when you receive it, please contact our Customer Care team at
            <strong> hello@aishcraft.com</strong> within <strong>7 days of receipt</strong> with
            your order number and photographs of the fault, and we will advise you further.
          </p>
          <p className="mt-3">
            Please do not return any faulty goods unless advised by a member of our customer service
            team, as it may result in a slower resolution time.
          </p>
          <ul className="list-disc pl-5 space-y-2 mt-3">
            <li>Faulty items must be reported within 7 days of delivery.</li>
            <li>Please include clear photographs of the fault when contacting us.</li>
            <li>We will offer a replacement, repair, or full refund at our discretion.</li>
            <li>Damage caused by the customer will not be treated as a manufacturing fault.</li>
            <li>If a replacement is not available, a full refund including return shipping will be issued.</li>
          </ul>
        </Section>

        {/* 4 */}
        <Section title="Non-Returnable Items">
          <p>
            Certain items are exempt from our standard returns policy. The following categories cannot
            be returned or refunded unless they arrive damaged or faulty:
          </p>
          <ul className="list-disc pl-5 space-y-2 mt-3">
            <li>Personalised or custom-made products (e.g. photobooks, custom stationery, monogrammed items).</li>
            <li>Gift Cards and store credit vouchers.</li>
            <li>Digital downloads or electronically delivered products once accessed.</li>
            <li>Items marked as "Final Sale" or sold at clearance prices.</li>
            <li>Perishable or consumable goods.</li>
            <li>Items that have been used, washed, damaged, or are missing original packaging.</li>
          </ul>
          <p className="mt-3">
            If you are unsure whether your item is eligible for a return, please contact us before
            placing your order.
          </p>
        </Section>

        {/* 5 */}
        <Section title="Exchanges">
          <p>
            We currently do not offer direct exchanges. If you would like a different product, size,
            or variant, please return your original item for a refund (subject to our returns policy)
            and place a new order separately.
          </p>
          <p className="mt-3">
            If your item arrived damaged or incorrect, we will arrange a replacement at no additional
            cost. Please contact hello@aishcraft.com within 7 days of receiving your order.
          </p>
        </Section>

        {/* 6 */}
        <Section title="Missing a Refund?">
          <p>
            If you haven't received your refund yet, please follow these steps before contacting us:
          </p>
          <ul className="list-disc pl-5 space-y-2 mt-3">
            <li>Check your bank account — refunds can sometimes take a few days to appear.</li>
            <li>Contact your card issuer or bank, as processing times vary between institutions.</li>
            <li>Check your original payment method (PayPal, UPI, bank transfer) for the refund.</li>
            <li>Allow up to 10 working days from our confirmation email before escalating.</li>
          </ul>
          <p className="mt-3">
            If you have done all of the above and still have not received your refund, please contact
            us at <strong>hello@aishcraft.com</strong> with your order number and refund confirmation
            email, and we will do our best to resolve the issue promptly.
          </p>
        </Section>

        {/* 7 */}
        <Section title="Cancelled Goods">
          <p>
            Under the Consumer Protection (Distance Selling Regulations) 2000, you have the legal
            right to cancel your contract within <strong>seven working days</strong> of the date you
            receive the Goods.
          </p>
          <p className="mt-3">
            To cancel, you must inform us in writing by sending an email to
            <strong> hello@aishcraft.com</strong> including your order number and the reason for
            cancellation. You must also return the goods to us as soon as possible after cancellation.
          </p>
          <ul className="list-disc pl-5 space-y-2 mt-3">
            <li>Cancellations must be made within 7 working days of receiving your order.</li>
            <li>The cost of returning cancelled goods is the responsibility of the customer.</li>
            <li>We suggest you obtain proof of postage in case your return gets lost in transit.</li>
            <li>You have a legal obligation to take reasonable care of the Goods while in your possession.</li>
            <li>Failure to take reasonable care of returned goods may result in a claim for compensation.</li>
            <li>Personalised or custom-made orders cannot be cancelled once production has begun.</li>
          </ul>
          <p className="mt-3">
            Once we receive your cancelled goods, we will process your full refund within 14 working
            days. This does not affect your statutory rights.
          </p>
        </Section>

        {/* 8 */}
        <Section title="Order Errors and Incorrect Items">
          <p>
            If you have received an incorrect item or your order is missing a product, please contact
            us at hello@aishcraft.com within <strong>48 hours of delivery</strong> with:
          </p>
          <ul className="list-disc pl-5 space-y-2 mt-3">
            <li>Your order number.</li>
            <li>A description of the issue.</li>
            <li>Clear photographs of what you received.</li>
          </ul>
          <p className="mt-3">
            We will investigate and, where confirmed, arrange for the correct item to be sent to you
            or issue a full refund at no additional cost.
          </p>
        </Section>

        {/* 9 */}
        <Section title="Damaged in Transit">
          <p>
            Although we take great care in packaging your orders, occasionally items may be damaged
            during delivery. If your order arrives damaged, please:
          </p>
          <ul className="list-disc pl-5 space-y-2 mt-3">
            <li>Do not discard the original packaging — it may be needed for a courier claim.</li>
            <li>Photograph the damaged packaging and the damaged item clearly.</li>
            <li>Contact us at hello@aishcraft.com within 48 hours of delivery.</li>
            <li>Include your order number, photos, and a brief description of the damage.</li>
          </ul>
          <p className="mt-3">
            We will work with you and our courier partners to resolve the issue as quickly as possible,
            either by sending a replacement or issuing a refund.
          </p>
        </Section>

        {/* 10 */}
        <Section title="Contact Us">
          <p>
            If you have any questions about our Refund Policy or need assistance with a return, please
            get in touch with our Customer Care team. We are here to help and will respond within
            1–2 business days.
          </p>
          <ul className="list-none pl-0 space-y-1 mt-3">
            <li><strong>Email:</strong> hello@aishcraft.com</li>
            <li><strong>Address:</strong> Aishcraft, Bhopal, Madhya Pradesh, India</li>
            <li><strong>Response Time:</strong> 1–2 business days</li>
          </ul>
        </Section>

      </div>
    </div>
  );
};

export default RefundPolicy;